import React, { useState, useEffect } from 'react';
import { Phone, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      // 2cm ≈ 75px für Desktop, 1cm ≈ 37px für Mobile
      const isMobile = window.innerWidth < 1024;
      const headerOffset = isMobile ? 37 : 75; // 1cm mobile, 2cm desktop
      
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path, sectionId = null) => {
    navigate(path);
    if (sectionId) {
      setTimeout(() => scrollToSection(sectionId), 100);
    }
    setMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-[#0A2846]/60 backdrop-blur-xl shadow-lg' 
          : 'bg-[#0A2846]/40 backdrop-blur-lg'
      }`}
      style={{
        backdropFilter: scrolled ? 'blur(24px) saturate(180%)' : 'blur(16px) saturate(150%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <div className="flex-shrink-0 cursor-pointer" onClick={handleLogoClick}>
            <img
              src="https://customer-assets.emergentagent.com/job_premium-reinigung/artifacts/7bybhkxc_8858D91C-BC6F-4317-A826-E2C324679F0A.PNG"
              alt="PROBAV Gebäudereinigung Logo"
              className="h-20 sm:h-24 lg:h-28 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => handleNavigation('/', 'services')}
              className="text-[#E8EEF3] hover:text-[#D4AF37] font-medium transition-colors duration-300"
            >
              Leistungen
            </button>
            <button
              onClick={() => handleNavigation('/', 'process')}
              className="text-[#E8EEF3] hover:text-[#D4AF37] font-medium transition-colors duration-300"
            >
              Ablauf
            </button>
            <button
              onClick={() => handleNavigation('/', 'why-us')}
              className="text-[#E8EEF3] hover:text-[#D4AF37] font-medium transition-colors duration-300"
            >
              Warum PROBAV
            </button>
            <button
              onClick={() => handleNavigation('/', 'about')}
              className="text-[#E8EEF3] hover:text-[#D4AF37] font-medium transition-colors duration-300"
            >
              Über uns
            </button>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:+491701247477">
              <Button
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A2846] transition-all duration-300 font-semibold"
                style={{ boxShadow: '0 0 15px rgba(212, 175, 55, 0.3)' }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Anrufen
              </Button>
            </a>
            <Button
              onClick={() => navigate('/kontakt')}
              className="font-semibold transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #F4D03F 100%)',
                color: '#0A2846',
                boxShadow: '0 4px 20px rgba(212, 175, 55, 0.4), 0 0 30px rgba(212, 175, 55, 0.2)'
              }}
            >
              Angebot anfordern
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-[#E8EEF3] p-2"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0A2846] border-t border-[#D4AF37]/20">
          <div className="px-4 pt-4 pb-6 space-y-3">
            <button
              onClick={() => handleNavigation('/', 'services')}
              className="block w-full text-left px-4 py-3 text-[#E8EEF3] hover:bg-[#D4AF37]/20 rounded-lg transition-colors duration-300"
            >
              Leistungen
            </button>
            <button
              onClick={() => handleNavigation('/', 'process')}
              className="block w-full text-left px-4 py-3 text-[#E8EEF3] hover:bg-[#D4AF37]/20 rounded-lg transition-colors duration-300"
            >
              Ablauf
            </button>
            <button
              onClick={() => handleNavigation('/', 'why-us')}
              className="block w-full text-left px-4 py-3 text-[#E8EEF3] hover:bg-[#D4AF37]/20 rounded-lg transition-colors duration-300"
            >
              Warum PROBAV
            </button>
            <button
              onClick={() => handleNavigation('/', 'about')}
              className="block w-full text-left px-4 py-3 text-[#E8EEF3] hover:bg-[#D4AF37]/20 rounded-lg transition-colors duration-300"
            >
              Über uns
            </button>
            <div className="pt-3 space-y-3">
              <a href="tel:+4917012474477" className="block">
                <Button
                  variant="outline"
                  className="w-full border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A2846] transition-all duration-300 font-semibold"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Anrufen
                </Button>
              </a>
              <Button
                onClick={() => { navigate('/kontakt'); setMobileMenuOpen(false); }}
                className="w-full font-semibold"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #F4D03F 100%)',
                  color: '#0A2846'
                }}
              >
                Angebot anfordern
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;